// The house colors: what a team wears before anybody picks anything (L2).
//
// WHY THIS EXISTS. Every seat a league was created with wore the same grey
// pair, and so did every bot that took one at the draft, which left a real
// league's scoreboard, and every screen like it, looking plain the morning
// after its draft. A league should look like twelve teams, not twelve names
// (SCENARIOS L2), and the colors are most of that.
//
// TWENTY PAIRS, ONE PER SEAT OF THE LARGEST LEAGUE. `identity.teamCount` stops
// at twenty, so no two seats of one league share a pair. The ORDER is the
// design: the first eight — the common league — are eight different hues, and
// the bright ones come first, because a dark primary barely tints a card on the
// dark theme and the first seats are the ones every league has. The dark pairs
// (navy, maroon, forest, plum, black) wait for the big leagues.
//
// CHECKED, NOT HOPED. `palette.test.ts` holds what the list promises: twenty
// entries, no primary twice, a badge's initials readable at 4.5:1 or better on
// every primary (with `onColor`, which is what `TeamMark` draws them in), and
// the first eight clearly apart from each other.
//
// A SPORT-FREE FACT. These are two hex colors and a name; nothing here knows
// what a pro team is. "Match a pro team" is the other way to choose, and a
// client resolves that from the sport's definition.
//
// THE PALETTE MAY CHANGE, AND A LEAGUE KEEPS WHAT IT WAS GIVEN. A team's colors
// are written onto its row when the seat is made (`createLeague`), so changing
// an entry here recolors nobody who already has it. Migration `0018` wrote this
// list into SQL as it stood on 2026-09-25 for the leagues made before it.

import type { TeamColors } from '../api/league';

/** One of the house pairs, as a screen offers it. */
export interface HouseColors {
  /** What a screen reader says for the swatch: "Blue and gold". */
  name: string;
  /** `#rrggbb`. The badge's fill and the tint on a matchup card. */
  primary: string;
  /** `#rrggbb`. The ring around the badge. */
  secondary: string;
}

/** The twenty, in seat order. See the header for why the order matters. */
export const HOUSE_COLORS: readonly HouseColors[] = [
  { name: 'Blue and gold', primary: '#2563EB', secondary: '#FBBF24' },
  { name: 'Red and cream', primary: '#DC2626', secondary: '#FDE68A' },
  { name: 'Green and lime', primary: '#16A34A', secondary: '#BEF264' },
  { name: 'Purple and pink', primary: '#7C3AED', secondary: '#F9A8D4' },
  { name: 'Orange and sky', primary: '#EA580C', secondary: '#7DD3FC' },
  { name: 'Cyan and apricot', primary: '#0891B2', secondary: '#FDBA74' },
  { name: 'Magenta and yellow', primary: '#C026D3', secondary: '#FDE047' },
  { name: 'Gold and blue', primary: '#CA8A04', secondary: '#1D4ED8' },
  { name: 'Pink and aqua', primary: '#DB2777', secondary: '#5EEAD4' },
  { name: 'Lime and cream', primary: '#65A30D', secondary: '#FEF9C3' },
  { name: 'Navy and orange', primary: '#1E3A8A', secondary: '#FB923C' },
  { name: 'Maroon and silver', primary: '#881337', secondary: '#CBD5E1' },
  { name: 'Sky and navy', primary: '#0EA5E9', secondary: '#1E3A8A' },
  { name: 'Teal and rose', primary: '#0F766E', secondary: '#FDA4AF' },
  { name: 'Coral and cream', primary: '#F43F5E', secondary: '#FEF3C7' },
  { name: 'Brown and ice', primary: '#92400E', secondary: '#BAE6FD' },
  { name: 'Forest and gold', primary: '#14532D', secondary: '#FACC15' },
  { name: 'Slate and red', primary: '#475569', secondary: '#F87171' },
  { name: 'Plum and mint', primary: '#701A75', secondary: '#86EFAC' },
  { name: 'Black and gold', primary: '#1F2937', secondary: '#FBBF24' },
];

/**
 * The pair a seat wears until its manager chooses: `seat` is 0-based, as
 * `teams.seat` is, and wraps past the twentieth.
 *
 * A NEW OBJECT EACH CALL, and only the two colors. What a store writes onto a
 * row is `TeamColors`; the name is for a screen that offers the list.
 */
export function houseColorsForSeat(seat: number): TeamColors {
  const n = HOUSE_COLORS.length;
  const entry = HOUSE_COLORS[((Math.trunc(seat) % n) + n) % n];
  // A seat that is not a number is a caller's bug, and a grey badge would hide it.
  if (entry === undefined) throw new Error(`houseColorsForSeat: ${seat} is not a seat`);
  return { primary: entry.primary, secondary: entry.secondary };
}

/**
 * Whether two pairs are the same colors. Hex case is spelling, not color: the
 * house pairs are written in capitals and a sport's palettes in lower case.
 */
export function sameColors(a: TeamColors, b: TeamColors): boolean {
  return (
    a.primary.toLowerCase() === b.primary.toLowerCase() &&
    a.secondary.toLowerCase() === b.secondary.toLowerCase()
  );
}
